import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Check, Loader2, ShieldCheck, Building2, Share2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const money = (n) => `$${Number(n || 0).toFixed(2)}`;
const PLAN_NAMES = { premium: 'Premium', family: 'Family' };

export default function GroupLanding() {
  const { code } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/membership/group/${code}`)
      .then((r) => setData(r.data)).catch(() => setErr(true)).finally(() => setLoading(false));
  }, [code]);

  const start = () => {
    localStorage.setItem('group_code', code.toUpperCase());
    const token = localStorage.getItem('session_token');
    navigate(token ? `/membership?group=${code.toUpperCase()}` : `/login?mode=signup&group=${code.toUpperCase()}`);
  };

  const share = async () => {
    const url = `${window.location.origin}/group/${code.toUpperCase()}`;
    const title = data ? `${data.company_name} — EasyPassing offer` : 'EasyPassing offer';
    const text = data ? `${data.company_name} members get ${label} on EasyPassing. Claim your discount:` : 'Claim your EasyPassing discount:';
    if (navigator.share) {
      try { await navigator.share({ title, text, url }); } catch (e) { /* user cancelled */ }
    } else {
      try { await navigator.clipboard.writeText(url); toast.success('Offer link copied to clipboard'); }
      catch (e) { toast.error('Could not copy link'); }
    }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-[#FDFBF7]"><Loader2 className="h-8 w-8 animate-spin text-[#2B4C7E]" /></div>;
  if (err || !data) return (
    <div className="min-h-screen flex items-center justify-center bg-[#FDFBF7] px-4" data-testid="group-landing-error">
      <div className="text-center"><h1 className="font-display text-2xl font-bold text-[#1C252E]">Offer not found</h1><p className="text-slate-500 mt-2">This group link isn't valid.</p><Button onClick={() => navigate('/')} className="mt-6 rounded-full bg-[#2B4C7E]">Go home</Button></div>
    </div>
  );

  const label = data.discount_type === 'percentage' ? `${data.discount_value}% off` : `${money(data.discount_value)} off`;
  return (
    <div className="min-h-screen bg-[#FDFBF7] overflow-x-hidden" data-testid="group-landing">
      <div className="bg-[#1E3A63] text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-14 text-center">
          <div className="inline-flex items-center gap-2 bg-white/10 px-4 py-1.5 rounded-full text-sm mb-5"><Building2 className="h-4 w-4 text-[#F4D9A0]" /> Exclusive offer for {data.company_name}</div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold">A special EasyPassing offer, just for your team.</h1>
          <p className="mt-4 text-[#DCE6F1] text-lg">{data.company_name} members get <b className="text-[#F4D9A0]">{label}</b> on {data.applies_to_plan === 'all' ? 'any plan' : `the ${PLAN_NAMES[data.applies_to_plan]} plan`}{data.applies_to_billing_cycle !== 'all' ? ` (${data.applies_to_billing_cycle})` : ''}.</p>
          {data.active
            ? <Button onClick={start} className="mt-8 rounded-full bg-[#C9A24B] hover:bg-[#b08f3f] text-white h-13 px-8 text-base font-semibold py-6" data-testid="group-start-btn">Claim your discount</Button>
            : <p className="mt-8 inline-block bg-white/10 px-5 py-3 rounded-xl text-[#F4D9A0] font-medium" data-testid="group-inactive">This offer is no longer available.</p>}
          {data.seats_left != null && data.active && <p className="mt-3 text-sm text-[#DCE6F1]">{data.seats_left} membership{data.seats_left === 1 ? '' : 's'} remaining</p>}
          <div className="mt-5">
            <button onClick={share} className="inline-flex items-center gap-2 text-sm text-[#DCE6F1] hover:text-white transition-colors" data-testid="group-share-btn">
              <Share2 className="h-4 w-4" /> Share this offer with your team
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-12">
        {data.plans.length > 0 && (
          <>
            <h2 className="font-display text-2xl font-bold text-[#1C252E] text-center mb-6">Your discounted pricing</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              {data.plans.map((p) => (
                <motion.div key={`${p.plan}-${p.billing_cycle}`} whileHover={{ y: -4 }} className="bg-white rounded-2xl border border-slate-100 p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
                  <p className="font-semibold text-[#1C252E]">{PLAN_NAMES[p.plan]} <span className="capitalize text-slate-400 text-sm">· {p.billing_cycle}</span></p>
                  <div className="mt-2 flex items-end gap-2">
                    <span className="font-display text-3xl font-bold text-[#2B4C7E]">{money(p.final)}</span>
                    <span className="text-slate-400 line-through mb-1">{money(p.original)}</span>
                  </div>
                  <p className="text-xs text-[#7FA98C] font-semibold mt-1">You save {money(p.discount)}</p>
                </motion.div>
              ))}
            </div>
          </>
        )}
        <div className="mt-10 bg-white rounded-2xl border border-slate-100 p-6">
          <p className="font-semibold text-[#1C252E] mb-3">How it works</p>
          <ol className="space-y-2 text-sm text-slate-600">
            <li className="flex gap-2"><Check className="h-5 w-5 text-[#2B4C7E] shrink-0" /> Create your free EasyPassing account.</li>
            <li className="flex gap-2"><Check className="h-5 w-5 text-[#2B4C7E] shrink-0" /> Your code <b className="font-mono">{data.code}</b> is applied automatically at checkout.</li>
            <li className="flex gap-2"><Check className="h-5 w-5 text-[#2B4C7E] shrink-0" /> Start protecting your family's legacy.</li>
          </ol>
        </div>
        {data.active && <div className="text-center mt-8"><Button onClick={start} className="rounded-full bg-[#2B4C7E] hover:bg-[#1E3A63] text-white h-12 px-8 font-semibold" data-testid="group-start-btn-2">Get started</Button></div>}
        <p className="text-center text-xs text-slate-400 mt-8 flex items-center justify-center gap-1.5"><ShieldCheck className="h-4 w-4" /> Bank-grade encryption · Cancel anytime</p>
      </div>
    </div>
  );
}
